import { createFileRoute } from "@tanstack/react-router";
import { TopBar } from "@/components/TopBar";
import {
  aprendizados as seedAprendizados,
  reguaRisco as seedRegua,
  clientNotes as seedNotes,
  getProject,
  type Aprendizado,
  type RiscoRegua,
} from "@/lib/mock-data";
import { Brain, AlertTriangle, TrendingUp, Flag, Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClientsKnowledge } from "@/components/ai/ClientsKnowledge";
import { FreelancersKnowledge } from "@/components/ai/FreelancersKnowledge";
import { FitWeightsPanel } from "@/components/ai/FitWeightsPanel";

import { useRole } from "@/lib/use-role";

export const Route = createFileRoute("/admin-ia")({
  component: AdminIAPage,
});

function AdminIAPage() {
  const role = useRole();
  const readOnly = !(role === "Admin" || role === "Gestor");

  const [notes, setNotes] = useState<Record<string, string[]>>(() => ({ ...seedNotes }));
  const [regua, setRegua] = useState<RiscoRegua[]>(() =>
    seedRegua.map((r) => ({ ...r, motivos: [...r.motivos] })),
  );
  const [aprend, setAprend] = useState<Aprendizado[]>(() => [...seedAprendizados]);

  const [addingFlagFor, setAddingFlagFor] = useState<string | null>(null);
  const [flagDraft, setFlagDraft] = useState("");
  const [addingRiskFor, setAddingRiskFor] = useState<string | null>(null);
  const [riskDraft, setRiskDraft] = useState("");
  const [aprendModalOpen, setAprendModalOpen] = useState(false);

  const addFlag = (cliente: string) => {
    const v = flagDraft.trim();
    if (!v) return;
    setNotes((prev) => ({ ...prev, [cliente]: [...(prev[cliente] ?? []), v] }));
    setFlagDraft("");
    setAddingFlagFor(null);
  };
  const removeFlag = (cliente: string, flag: string) => {
    setNotes((prev) => ({ ...prev, [cliente]: (prev[cliente] ?? []).filter((f) => f !== flag) }));
  };

  const addRisk = (projetoId: string) => {
    const v = riskDraft.trim();
    if (!v) return;
    setRegua((prev) =>
      prev.map((r) =>
        r.projetoId === projetoId
          ? { ...r, motivos: [...r.motivos, v], risco: Math.min(100, r.risco + 5) }
          : r,
      ),
    );
    setRiskDraft("");
    setAddingRiskFor(null);
  };
  const removeRisk = (projetoId: string, motivo: string) => {
    setRegua((prev) =>
      prev.map((r) =>
        r.projetoId === projetoId
          ? {
              ...r,
              motivos: r.motivos.filter((m) => m !== motivo),
              risco: Math.max(0, r.risco - 5),
            }
          : r,
      ),
    );
  };

  return (
    <>
      <TopBar
        title="Alimentação da IA"
        subtitle="Base de conhecimento · clientes, freelancers, risco e motor de fit"
      />
      <div className="flex-1 overflow-auto p-4">
        <Tabs defaultValue="clientes" className="space-y-4">
          <TabsList>
            <TabsTrigger value="clientes">Clientes</TabsTrigger>
            <TabsTrigger value="freelancers">Freelancers</TabsTrigger>
            <TabsTrigger value="risco">Risco e Aprendizados</TabsTrigger>
            <TabsTrigger value="motor">Motor de Fit</TabsTrigger>
          </TabsList>

          <TabsContent value="clientes">
            <ClientsKnowledge readOnly={readOnly} />
          </TabsContent>
          <TabsContent value="freelancers">
            <FreelancersKnowledge readOnly={readOnly} />
          </TabsContent>
          <TabsContent value="motor">
            <FitWeightsPanel readOnly={readOnly} />
          </TabsContent>

          <TabsContent value="risco" className="space-y-4">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {/* Régua de risco */}
              <section className="rounded-xl border border-border bg-card p-4">
                <div className="mb-3 flex items-center gap-2">
                  <Flag className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">
                    Régua de risco por projeto
                  </h3>
                </div>
                <div className="space-y-3">
                  {regua.map((r) => {
                    const p = getProject(r.projetoId);
                    if (!p) return null;
                    const tone =
                      r.risco >= 75
                        ? "bg-health-red"
                        : r.risco >= 50
                          ? "bg-health-yellow"
                          : "bg-primary";
                    const isAdding = addingRiskFor === r.projetoId;
                    return (
                      <div
                        key={r.projetoId}
                        className="rounded-lg border border-border bg-secondary/30 p-3"
                      >
                        <div className="flex items-center justify-between">
                          <div className="min-w-0">
                            <div className="text-xs font-semibold text-foreground">{p.cliente}</div>
                            <div className="truncate text-[11px] text-muted-foreground">
                              {p.nome}
                            </div>
                          </div>
                          <span className="text-lg font-bold tabular-nums text-foreground">
                            {r.risco}
                          </span>
                        </div>
                        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-background">
                          <div
                            className={cn("h-full transition-all", tone)}
                            style={{ width: `${r.risco}%` }}
                          />
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {r.motivos.map((m) => (
                            <span
                              key={m}
                              className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-1.5 py-0.5 text-[10px] text-muted-foreground"
                            >
                              {m}
                              <button
                                type="button"
                                onClick={() => removeRisk(r.projetoId, m)}
                                className="text-muted-foreground hover:text-health-red"
                                aria-label={`Remover ${m}`}
                              >
                                <X className="h-2.5 w-2.5" />
                              </button>
                            </span>
                          ))}
                        </div>
                        {isAdding ? (
                          <div className="mt-2 flex gap-1.5">
                            <Input
                              autoFocus
                              value={riskDraft}
                              onChange={(e) => setRiskDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") addRisk(r.projetoId);
                                if (e.key === "Escape") {
                                  setAddingRiskFor(null);
                                  setRiskDraft("");
                                }
                              }}
                              placeholder="ex: prazo crítico"
                              className="h-7 text-xs"
                            />
                            <Button
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={() => addRisk(r.projetoId)}
                            >
                              OK
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs"
                              onClick={() => {
                                setAddingRiskFor(null);
                                setRiskDraft("");
                              }}
                            >
                              Cancelar
                            </Button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setAddingRiskFor(r.projetoId);
                              setRiskDraft("");
                            }}
                            className="mt-2 inline-flex items-center gap-1 text-[10px] font-medium text-primary hover:underline"
                          >
                            <Plus className="h-3 w-3" /> Adicionar fator de risco
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Redflags de clientes */}
              <section className="rounded-xl border border-border bg-card p-4">
                <div className="mb-3 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Redflags de clientes</h3>
                </div>
                <div className="space-y-2">
                  {Object.entries(notes).map(([cliente, flags]) => {
                    const isAdding = addingFlagFor === cliente;
                    return (
                      <div
                        key={cliente}
                        className="rounded-lg border border-border bg-secondary/30 p-3"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-xs font-semibold text-foreground">{cliente}</div>
                          <button
                            type="button"
                            onClick={() => {
                              setAddingFlagFor(cliente);
                              setFlagDraft("");
                            }}
                            className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-border bg-background text-muted-foreground hover:text-primary"
                            aria-label={`Adicionar redflag para ${cliente}`}
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {flags.map((f) => (
                            <span
                              key={f}
                              className="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/15 px-2 py-0.5 text-[10px] font-medium text-primary"
                            >
                              {f}
                              <button
                                type="button"
                                onClick={() => removeFlag(cliente, f)}
                                className="text-primary/70 hover:text-primary"
                                aria-label={`Remover ${f}`}
                              >
                                <X className="h-2.5 w-2.5" />
                              </button>
                            </span>
                          ))}
                          {flags.length === 0 && !isAdding && (
                            <span className="text-[10px] text-muted-foreground">Sem redflags.</span>
                          )}
                        </div>
                        {isAdding && (
                          <div className="mt-2 flex gap-1.5">
                            <Input
                              autoFocus
                              value={flagDraft}
                              onChange={(e) => setFlagDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") addFlag(cliente);
                                if (e.key === "Escape") {
                                  setAddingFlagFor(null);
                                  setFlagDraft("");
                                }
                              }}
                              placeholder="ex: sempre atrasa aprovação"
                              className="h-7 text-xs"
                            />
                            <Button
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={() => addFlag(cliente)}
                            >
                              OK
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs"
                              onClick={() => {
                                setAddingFlagFor(null);
                                setFlagDraft("");
                              }}
                            >
                              Cancelar
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>

            {/* Aprendizados */}
            <section className="rounded-xl border border-border bg-card p-4">
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Brain className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">
                    Aprendizados — planejado × realizado
                  </h3>
                </div>
                <Button
                  size="sm"
                  className="h-7 gap-1 text-xs"
                  onClick={() => setAprendModalOpen(true)}
                >
                  <Plus className="h-3 w-3" /> Adicionar aprendizado
                </Button>
              </div>
              <div className="overflow-hidden rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Cliente</th>
                      <th className="px-3 py-2 text-right font-medium">Planejado (h)</th>
                      <th className="px-3 py-2 text-right font-medium">Realizado (h)</th>
                      <th className="px-3 py-2 text-right font-medium">Variação</th>
                      <th className="px-3 py-2 text-left font-medium">Insight</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {aprend.map((a, idx) => {
                      const diff = a.realizadoH - a.planejadoH;
                      const pct = a.planejadoH > 0 ? Math.round((diff / a.planejadoH) * 100) : 0;
                      const tone =
                        diff > 0
                          ? "text-health-red"
                          : diff < 0
                            ? "text-health-green"
                            : "text-foreground";
                      return (
                        <tr key={`${a.projetoId}-${idx}`}>
                          <td className="px-3 py-2.5 text-xs text-foreground">{a.cliente}</td>
                          <td className="px-3 py-2.5 text-right text-xs tabular-nums text-muted-foreground">
                            {a.planejadoH}
                          </td>
                          <td className="px-3 py-2.5 text-right text-xs tabular-nums text-muted-foreground">
                            {a.realizadoH}
                          </td>
                          <td
                            className={cn(
                              "px-3 py-2.5 text-right text-xs font-semibold tabular-nums",
                              tone,
                            )}
                          >
                            <span className="inline-flex items-center gap-1">
                              <TrendingUp className="h-3 w-3" />
                              {pct > 0 ? `+${pct}%` : `${pct}%`}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-xs text-muted-foreground">{a.insight}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          </TabsContent>
        </Tabs>
      </div>

      <AprendizadoModal
        open={aprendModalOpen}
        onOpenChange={setAprendModalOpen}
        onSave={(a) => {
          setAprend((prev) => [...prev, a]);
          setAprendModalOpen(false);
        }}
      />
    </>
  );
}

function AprendizadoModal({
  open,
  onOpenChange,
  onSave,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSave: (a: Aprendizado) => void;
}) {
  const [cliente, setCliente] = useState("");
  const [planejado, setPlanejado] = useState("");
  const [realizado, setRealizado] = useState("");
  const [insight, setInsight] = useState("");

  const reset = () => {
    setCliente("");
    setPlanejado("");
    setRealizado("");
    setInsight("");
  };

  const handleSave = () => {
    const ph = Number(planejado);
    const rh = Number(realizado);
    if (!cliente.trim() || !Number.isFinite(ph) || ph <= 0 || !Number.isFinite(rh) || rh < 0)
      return;
    onSave({
      projetoId: `manual-${Date.now()}`,
      cliente: cliente.trim(),
      planejadoH: ph,
      realizadoH: rh,
      insight: insight.trim() || "—",
    });
    reset();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Adicionar aprendizado</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="ap-cliente" className="text-xs">
              Cliente
            </Label>
            <Input
              id="ap-cliente"
              value={cliente}
              onChange={(e) => setCliente(e.target.value)}
              placeholder="ex: Stone"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ap-plan" className="text-xs">
                Horas planejadas
              </Label>
              <Input
                id="ap-plan"
                type="number"
                min={1}
                value={planejado}
                onChange={(e) => setPlanejado(e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ap-real" className="text-xs">
                Horas realizadas
              </Label>
              <Input
                id="ap-real"
                type="number"
                min={0}
                value={realizado}
                onChange={(e) => setRealizado(e.target.value)}
                placeholder="0"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ap-insight" className="text-xs">
              Insight
            </Label>
            <Textarea
              id="ap-insight"
              value={insight}
              onChange={(e) => setInsight(e.target.value)}
              placeholder="O que aprendemos com esse projeto?"
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
